"use client";

import { Modal } from "components/ui/v2";
import SVG from "components/ui/v2/svg-v2";
import useTranslation from "next-translate/useTranslation";
import type { ReactNode } from "react";

export interface ExploreRankingModalV2Props {
  open: boolean;
  onClose: () => void;
  title: string;
  iconSrc: string;
  description?: string;
  /** Optional controls to the left of the close button (e.g. filter tabs). */
  headerRight?: ReactNode;
  children: ReactNode;
}

/**
 * Variante D §5.5.1 — ranking Explore sin tabs (o con `headerRight` para filtros).
 * Shell canónico `Modal` v2 (reemplaza puente ExploreModal + openModal).
 */
export function ExploreRankingModalV2({
  open,
  onClose,
  title,
  iconSrc,
  description,
  headerRight,
  children,
}: ExploreRankingModalV2Props) {
  const { t } = useTranslation("common");

  const header = (
    <div className="flex w-full items-center justify-between gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-indigo-500/90 dark:text-indigo-300/80">
          {t("Explorar")}
        </p>
        <h2 className="mt-0.5 flex items-center gap-1.5 text-[13px] font-semibold leading-snug text-slate-900 dark:text-zinc-50 sm:text-[15px]">
          <SVG
            src={iconSrc}
            width={16}
            height={16}
            className="shrink-0 opacity-90"
          />
          <span className="min-w-0 truncate">{title}</span>
        </h2>
        {description ? (
          <p className="mt-1 max-w-2xl text-[11px] text-slate-500 dark:text-zinc-400 sm:text-xs">
            {description}
          </p>
        ) : null}
      </div>
      {headerRight ? (
        <div className="mr-8 shrink-0">{headerRight}</div>
      ) : null}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      backdrop="static"
      title={title}
      header={header}
      panelClassName="flex max-h-[min(88vh,780px)] max-w-2xl flex-col"
      headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
      bodyScrollFade
      bodyClassName="bg-gradient-to-b from-transparent via-white/95 via-[10%] to-white/95 to-[16%] py-3 dark:via-zinc-950/95 dark:to-zinc-950/95 sm:py-3.5"
    >
      {children}
    </Modal>
  );
}

export default ExploreRankingModalV2;
